import React, { useMemo, useCallback } from 'react';
import { Box, Flex, Text, ActionIcon, ScrollArea, Tooltip, Switch } from '@mantine/core';
import { TbTrash, TbChevronDown, TbPencil } from 'react-icons/tb';
import ExcelIcon from '../../../../assets/icons/excel.svg?react';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { idCreator } from '../../../Project/utils/idCreator';

function groupByCategory(files) {
  const result = {};

  for (const file of files) {
    for (const sheet of file.sheets) {
      for (const extraction of sheet.extractions) {
        const category = extraction.category || 'Uncategorized';

        if (!result[category]) {
          result[category] = {};
        }

        const fileKey = file.name;
        if (!result[category][fileKey]) {
          result[category][fileKey] = {
            fileType: file.type,
            sheets: {}
          };
        }

        if (!result[category][fileKey].sheets[sheet.name]) {
          result[category][fileKey].sheets[sheet.name] = {
            id: sheet.id,
            extractions: []
          };
        }

        result[category][fileKey].sheets[sheet.name].extractions.push(extraction);
      }
    }
  }

  return result;
}

export function SavedTemplates({
  idPrefix,
  savedFiles,
  onClose,
  onActionDelete,
  onEdit,
  hasMutateMode = true,
  hasPermissionToModify = true,
  activeFiles = [],
  setActiveFiles = () => {},
  withCategory = false,
  setWithCategory = () => {}
}) {
  const rootId = idPrefix || 'saved-templates';

  const handleAccordionToggle = useCallback(
    id => {
      if (activeFiles.includes(id)) {
        setActiveFiles(prev => prev.filter(item => item !== id));
      } else {
        setActiveFiles(prev => [...prev, id]);
      }
    },
    [activeFiles]
  );

  const handleCategoryViewToggle = useCallback(
    event => {
      setWithCategory(event.currentTarget.checked);
      setActiveFiles([]);
    },
    [setWithCategory, setActiveFiles]
  );

  const handleEditAction = useCallback(
    (sheetId, extractionId) => {
      onEdit(sheetId, extractionId);
    },
    [onEdit]
  );

  const handleDeleteAction = useCallback(
    extraction => {
      onActionDelete({
        ...extraction,
        type: 'delete-extract'
      });
    },
    [onActionDelete]
  );

  const extractedFiles = useMemo(() => {
    if (savedFiles)
      return savedFiles
        ?.map(item => ({
          ...item,
          sheets: item.sheets.filter(value => value.extractions.length > 0)
        }))
        ?.filter(item => item.sheets.length > 0);

    return [];
  }, [savedFiles]);

  const extractedCategories = useMemo(() => {
    if (savedFiles) {
      const grouped = groupByCategory(savedFiles);

      return grouped;
    }
    return {};
  }, [savedFiles]);

  return (
    <Box id={`${rootId}-root`} p="4px">
      <Flex id={`${rootId}-header`} mb="16px" justify="space-between" gap="8px" align="center">
        <Text id={`${rootId}-title`} fz="16px" lh="22px" c="gray.7">
          Completed Extractions
        </Text>
        <Flex id={`${rootId}-category-toggle`} align="center" gap="12px">
          <Text id={`${rootId}-category-toggle-label`} fz="12px" c="dark.9" lh="17px">
            Data Category View
          </Text>
          <Switch
            id={`${rootId}-category-toggle-switch`}
            size="xs"
            checked={withCategory}
            onChange={handleCategoryViewToggle}
          />
        </Flex>
      </Flex>
      {withCategory ? (
        <ScrollArea.Autosize scrollbarSize={14} scrollHideDelay={500} mah="45vh">
          {Object.keys(extractedCategories).map(category => (
            <Box id={`${rootId}-category-${idCreator(category)}`} key={category}>
              <Flex
                id={`${rootId}-category-row-${idCreator(category)}`}
                align="center"
                onClick={() => handleAccordionToggle(category)}
                justify="space-between"
                p="8px"
                sx={() => ({
                  cursor: 'pointer'
                })}
              >
                <Text
                  id={`${rootId}-category-name-${idCreator(category)}`}
                  maw="270px"
                  truncate="end"
                  fz="14px"
                  fw="600"
                >
                  {category}
                </Text>
                <TbChevronDown style={{ minWidth: '16px' }} />
              </Flex>
              {activeFiles.includes(category) &&
                Object.keys(extractedCategories[category]).map(fileName => (
                  <Box
                    id={`${rootId}-category-file-${idCreator(category)}-${idCreator(fileName)}`}
                    key={fileName + category}
                  >
                    <Tooltip multiline maw="350px" label={fileName} disabled={fileName.length < 45}>
                      <Flex
                        id={`${rootId}-category-file-row-${idCreator(category)}-${idCreator(fileName)}`}
                        py="8px"
                        pr="8px"
                        pl="16px"
                        gap="4px"
                        align="center"
                      >
                        <ExcelIcon style={{ minWidth: '16px' }} />
                        <Text
                          id={`${rootId}-category-file-name-${idCreator(category)}-${idCreator(fileName)}`}
                          maw="320px"
                          truncate="end"
                          fz="14px"
                        >
                          {fileName}
                        </Text>
                      </Flex>
                    </Tooltip>
                    <Flex direction="column">
                      {Object.keys(extractedCategories[category][fileName].sheets)?.map(
                        (sheetName, index) => {
                          const sheet = extractedCategories[category][fileName].sheets[sheetName];
                          return (
                            <Flex
                              id={`${rootId}-category-sheet-row-${idCreator(category)}-${idCreator(fileName)}-${sheet.id}`}
                              align="center"
                              p="8px 8px 8px 32px"
                              key={sheet.id}
                              justify="space-between"
                            >
                              <Text
                                id={`${rootId}-category-sheet-name-${idCreator(category)}-${idCreator(fileName)}-${sheet.id}`}
                                fz="14px"
                              >
                                {index + 1}.Sheet: {sheetName}
                              </Text>
                              <Flex
                                id={`${rootId}-category-sheet-actions-${idCreator(category)}-${idCreator(fileName)}-${sheet.id}`}
                                align="center"
                                gap="6px"
                              >
                                <ActionIcon
                                  id={`${rootId}-category-sheet-edit-${sheet.id}`}
                                  onClick={() =>
                                    handleEditAction(sheet.id, sheet.extractions[0].id)
                                  }
                                  variant="white"
                                  color="gray"
                                  size="xs"
                                >
                                  <TbPencil />
                                </ActionIcon>
                                <PermissionGuard allowed={hasPermissionToModify}>
                                  <ActionIcon
                                    id={`${rootId}-category-sheet-delete-${sheet.id}`}
                                    onClick={() => handleDeleteAction(sheet.extractions[0])}
                                    variant="white"
                                    color="gray"
                                    size="xs"
                                  >
                                    <TbTrash />
                                  </ActionIcon>
                                </PermissionGuard>
                              </Flex>
                            </Flex>
                          );
                        }
                      )}
                    </Flex>
                  </Box>
                ))}
            </Box>
          ))}
        </ScrollArea.Autosize>
      ) : (
        <ScrollArea.Autosize scrollbarSize={14} scrollHideDelay={500} mah="45vh">
          {extractedFiles?.map(item => (
            <Box id={`${rootId}-file-${item.id}`} key={item.id}>
              <Tooltip multiline maw="350px" label={item.name} disabled={item.name.length < 36}>
                <Flex
                  id={`${rootId}-file-row-${item.id}`}
                  align="center"
                  onClick={() => handleAccordionToggle(item.id)}
                  justify="space-between"
                  p="8px"
                  sx={() => ({
                    cursor: 'pointer'
                  })}
                >
                  <Flex id={`${rootId}-file-name-wrap-${item.id}`} align="center" gap="4px">
                    <ExcelIcon style={{ minWidth: '16px' }} />
                    <Text
                      id={`${rootId}-file-name-${item.id}`}
                      maw="270px"
                      truncate="end"
                      fz="14px"
                      fw="600"
                    >
                      {item.name}
                    </Text>
                  </Flex>
                  <TbChevronDown style={{ minWidth: '16px' }} />
                </Flex>
              </Tooltip>
              {activeFiles?.includes(item.id) &&
                item?.sheets?.map((sheet, index) => (
                  <Box id={`${rootId}-file-sheet-${item.id}-${sheet.id}`} key={sheet.id}>
                    <Text
                      id={`${rootId}-file-sheet-name-${item.id}-${sheet.id}`}
                      fz="14px"
                      p="8px 8px 8px 16px"
                    >
                      {index + 1}. Tab: {sheet.name}
                    </Text>
                    <Flex direction="column">
                      {sheet?.extractions?.map(category => (
                        <Flex
                          id={`${rootId}-file-category-row-${item.id}-${category.id}`}
                          align="center"
                          p="8px 8px 8px 32px"
                          key={category.id}
                          justify="space-between"
                        >
                          <Text
                            id={`${rootId}-file-category-name-${item.id}-${category.id}`}
                            fz="14px"
                          >
                            Data Category: {category.category}
                          </Text>
                          <Flex
                            id={`${rootId}-file-category-actions-${item.id}-${category.id}`}
                            align="center"
                            gap="6px"
                          >
                            <ActionIcon
                              id={`${rootId}-file-category-edit-${category.id}`}
                              onClick={() => handleEditAction(sheet.id, category.id)}
                              variant="white"
                              color="gray"
                              size="xs"
                            >
                              <TbPencil />
                            </ActionIcon>
                            {hasMutateMode && (
                              <PermissionGuard allowed={hasPermissionToModify}>
                                <ActionIcon
                                  id={`${rootId}-file-category-delete-${category.id}`}
                                  onClick={() => handleDeleteAction(category)}
                                  variant="white"
                                  color="gray"
                                  size="xs"
                                >
                                  <TbTrash />
                                </ActionIcon>
                              </PermissionGuard>
                            )}
                          </Flex>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>
                ))}
            </Box>
          ))}
        </ScrollArea.Autosize>
      )}
    </Box>
  );
}
